/**
 * Portal-side mirror of the backend `TriggerInputType` enum
 * (`business-layer/domain/workflow/types.ts`). There is no shared package for workflow
 * domain types, so the two are kept byte-identical by hand — the same duplication the
 * SLA routing policy types live with.
 */
export enum WorkflowTriggerType {
  InboundPhoneCall = 'InboundPhoneCall',
  InboundSms = 'InboundSms',
}

/** Display order for the surfaces that render one row per trigger. */
export const ORDERED_WORKFLOW_TRIGGER_TYPES: readonly WorkflowTriggerType[] = [
  WorkflowTriggerType.InboundPhoneCall,
  WorkflowTriggerType.InboundSms,
];

export const WORKFLOW_TRIGGER_LABELS: Record<WorkflowTriggerType, string> = {
  [WorkflowTriggerType.InboundPhoneCall]: 'Incoming call',
  [WorkflowTriggerType.InboundSms]: 'Incoming SMS',
};

/**
 * Node type carrying the trigger configuration, mirroring the backend's
 * `NodeType.Trigger`. Declared here rather than imported so this module stays free of
 * page-level dependencies and safe to pull into any surface.
 */
const TRIGGER_NODE_TYPE = 'trigger';

interface DefinitionShape {
  nodes?: { type?: unknown; data?: { triggerType?: unknown } }[];
}

/**
 * Reads the trigger type out of a raw workflow version definition.
 *
 * A trigger node carrying no `triggerType` resolves to `InboundPhoneCall`, matching the
 * backend's `matchesTriggerType`: legacy definitions predate the field and are treated
 * as voice. Divergence here would make the portal offer a workflow for a trigger the
 * backend never resolves it for.
 *
 * Returns `undefined` when the definition has no trigger node, names a trigger type this
 * portal build does not know, or cannot be parsed — a workflow the resolver cannot place
 * must not be offered as an override target.
 */
export function getWorkflowTriggerType(
  rawDefinition: string | null | undefined,
): WorkflowTriggerType | undefined {
  const triggerNode = findTriggerNode(rawDefinition);

  if (!triggerNode) return undefined;

  const configuredTriggerType = triggerNode.data?.triggerType;

  if (configuredTriggerType === undefined) return WorkflowTriggerType.InboundPhoneCall;

  return isWorkflowTriggerType(configuredTriggerType) ? configuredTriggerType : undefined;
}

function findTriggerNode(rawDefinition: string | null | undefined) {
  if (!rawDefinition) return undefined;

  try {
    const parsed = JSON.parse(rawDefinition) as DefinitionShape;
    return parsed?.nodes?.find((node) => node?.type === TRIGGER_NODE_TYPE);
  } catch {
    return undefined;
  }
}

function isWorkflowTriggerType(value: unknown): value is WorkflowTriggerType {
  return (
    typeof value === 'string' &&
    (Object.values(WorkflowTriggerType) as string[]).includes(value)
  );
}
